/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import axios from "axios";
import { AppThunk } from "../../../config/store";
import { Account, IBrand, IFeature, LoginAction } from "./slices";
import { ILogin, IPasswordReset } from "../../../types/login";
import { authenticatedUserApi, brandApi } from "../../../config/api";
import { get } from "@github/webauthn-json";
import { axiosInstanceRegion } from "../../../config/axios-region";
import { ErrorAction } from "@src/reducers/slices/errorSlice";
import { checkAcceptEula } from "@src/reducers/operations/eulaOperation";
import { API_BASE_URL, APP_ENVIRONMENT } from "@src/config/constants";
import { exposeCaptchaHeaders, IErrorMessage } from "@advapacs/common";

export const getOrganisationSettings =
  (organisationId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(LoginAction.getOrganisationSettingsStart());
      const userResponse = await axios
        .create()
        .get(authenticatedUserApi.CONFIGURATION(organisationId.toLowerCase()));
      dispatch(LoginAction.getOrganisationSettingsSuccess(userResponse?.data));
      // Concurrently next action.
      return await Promise.resolve();
    } catch (err) {
      dispatch(
        LoginAction.getOrganisationSettingsFail(
          (err as IErrorMessage)?.response?.data
        )
      );
      return Promise.reject();
    }
  };

// @ts-ignore
export const refreshToken = (region: string): Promise<void> => {
  try {
    return axios.post(`${authenticatedUserApi.REFRESH_TOKEN}?region=${region}`);
  } catch (error) {}
};

export const getUser = (): AppThunk => async (dispatch) => {
  try {
    dispatch(LoginAction.startFetchSession());
    const userResponse = await axios.get(authenticatedUserApi.GET_USER);
    dispatch(LoginAction.loginSuccess(userResponse?.data as Account));
  } catch (err) {
    dispatch(LoginAction.failFetchSession());
  }
};

export const getCsrfToken =
  (shouldCallGetUser?: boolean): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(LoginAction.getCsrfTokenStart());
      const crsfResponse = await axios.post(`/csrf`);
      dispatch(LoginAction.getCsrfTokenSuccess(crsfResponse?.data));
    } catch (err) {
      dispatch(
        LoginAction.getCsrfTokenFail((err as IErrorMessage)?.response?.data)
      );
    } finally {
      if (shouldCallGetUser) dispatch(getUser());
    }
  };

export const submitTwoFaLogin =
  (
    props: ILogin,
    pathName: string,
    recaptchaToken: string,
    turnstile?: boolean
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(LoginAction.registerTwoFaStart());
      await axios.post(authenticatedUserApi.AUTHENTICATE_USER, props, {
        // @ts-ignore
        headers: exposeCaptchaHeaders(turnstile, recaptchaToken),
      });
      dispatch(getUser());
    } catch (err) {
      dispatch(
        LoginAction.loginFail({
          err: (err as IErrorMessage)?.response?.data,
          pathname: pathName,
        })
      );
    }
  };

export const logout = (): AppThunk => async (dispatch) => {
  try {
    await axios.post(`/auth/logout`);
    dispatch(LoginAction.clearAuthentication({ isReloadBrowser: true }));
  } catch (err) {
    dispatch(LoginAction.clearAuthentication({ isReloadBrowser: true }));
  }
};

export const submitLoginRetry = async (
  // @ts-ignore
  dispatch,
  props: ILogin,
  recaptchaToken: string,
  turnstile?: boolean
) => {
  try {
    dispatch(LoginAction.loginStart());
    await axios.post(authenticatedUserApi.AUTHENTICATE_USER, props, {
      // @ts-ignore
      headers: exposeCaptchaHeaders(turnstile, recaptchaToken),
    });
    dispatch(getUser());
    dispatch(checkAcceptEula());
  } catch (error) {
    return Promise.reject((error as IErrorMessage)?.response?.data);
  }
};

export const submitLogin =
  (
    props: ILogin,
    pathname: string,
    recaptchaToken: string,
    turnstile?: boolean
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(LoginAction.loginStart());
      await axios.post(authenticatedUserApi.AUTHENTICATE_USER, props, {
        // @ts-ignore
        headers: exposeCaptchaHeaders(turnstile, recaptchaToken),
      });
      dispatch(getUser());
      dispatch(checkAcceptEula());
    } catch (err) {
      dispatch(
        LoginAction.loginFail({
          err: (err as IErrorMessage)?.response?.data,
          pathname,
        })
      );
    }
  };

export const getTwoFaStatus = (): AppThunk => async (dispatch) => {
  try {
    dispatch(LoginAction.getTwoFaStatusStart());
    const result = await axios.get(`/security/twoFactor`);
    dispatch(LoginAction.getTwoFaStatusSuccess(result?.data?.totp));
  } catch (err) {
    dispatch(
      LoginAction.getTwoFaStatusFail((err as IErrorMessage)?.response?.data)
    );
  }
};

export const registerTwoFa =
  (pinCode: number, otpSecret?: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(LoginAction.registerTwoFaStart());
      await axios.post(`/security/twoFactor/totp`, {
        token: pinCode,
        secret: otpSecret,
      });
      dispatch(LoginAction.getTwoFaStatusSuccess(true));
      if (window.location.pathname.indexOf("/UserSettings") < 0) {
        // @ts-ignore
        refreshToken(localStorage.getItem("region"))
          .then(() => {})
          .finally(() => {
            setTimeout(() => {
              dispatch(getUser());
              // redirect 2FA it if require !!!
            }, 10);
          });
      } else {
        dispatch(getTwoFaStatus());
      }
    } catch (err) {
      dispatch(
        LoginAction.registerTwoFaFail((err as IErrorMessage)?.response?.data)
      );
    }
  };

export const unRegisterTwoFa = (): AppThunk => async (dispatch) => {
  try {
    dispatch(LoginAction.registerTwoFaStart());
    await axios.delete(`/security/twoFactor/totp`);
    dispatch(LoginAction.getTwoFaStatusSuccess(false));
    if (window.location.pathname.indexOf("/UserSettings") < 0) {
      dispatch(getUser());
      // @ts-ignore
      refreshToken(localStorage.getItem("region"))
        .then(() => {})
        .finally(() => {
          setTimeout(() => {
            dispatch(getUser());
            // redirect 2FA it if require !!!
          }, 10);
        });
    } else {
      dispatch(getTwoFaStatus());
    }
  } catch (err) {
    dispatch(
      LoginAction.registerTwoFaFail((err as IErrorMessage)?.response?.data)
    );
  }
};

export const requestPasswordReset =
  (username: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(LoginAction.requestPasswordResetStart());
      await axios.post(`/auth/forgotPassword`, { username });
      dispatch(LoginAction.requestPasswordResetSuccess());
    } catch (err) {
      dispatch(
        LoginAction.requestPasswordResetFail(
          (err as IErrorMessage)?.response?.data
        )
      );
      dispatch(ErrorAction.setError([]));
    }
  };

export const forgotOrganisation =
  (email: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(LoginAction.forgotOrganisationStart());
      await axios.post(authenticatedUserApi.FORGOT_ORGANISATION, { email });
      dispatch(LoginAction.forgotOrganisationSuccess());
    } catch (err) {
      dispatch(
        LoginAction.forgotOrganisationFail(
          (err as IErrorMessage)?.response?.data
        )
      );
      dispatch(ErrorAction.setError([]));
    }
  };

export const passwordReset =
  (newPassword: IPasswordReset): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(LoginAction.requestPasswordResetStart());
      await axios.post(`/auth/resetPassword`, newPassword);
      dispatch(LoginAction.requestPasswordResetSuccess());
    } catch (err) {
      dispatch(
        LoginAction.requestPasswordResetFail(
          (err as IErrorMessage)?.response?.data
        )
      );
    }
  };

export const verifyToken =
  (token: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(LoginAction.verifyTokenStart());
      await axios.post(`/auth/verifyToken`, {
        token,
      });
      dispatch(LoginAction.verifyTokenSuccess());
    } catch (err) {
      dispatch(
        LoginAction.verifyTokenFail((err as IErrorMessage)?.response?.data)
      );
    }
  };

export const webAuthnAuthenticate =
  (
    login: ILogin,
    challenge: string,
    pathName: string,
    recaptchaToken: string,
    turnstile?: boolean
  ): AppThunk =>
  async (dispatch) => {
    dispatch(LoginAction.registerTwoFaStart());
    try {
      const content = JSON.parse(challenge);
      const {
        requestJSON,
        AssertionRequestWrapper,
        publicKeyCredentialRequestOptions,
      } = content;
      /**
       * WebAuthn v2
       */
      if (requestJSON) {
        const credentialRequest = JSON.parse(content.requestJSON);
        const credentialGetOptions = {
          publicKey: credentialRequest.publicKeyCredentialRequestOptions,
        };
        const credentialResponse = await get(credentialGetOptions);
        const verifyingWebauthnResponsePayload = {
          requestId: content.requestId,
          credential: credentialResponse,
        };
        dispatch(
          submitTwoFaLogin(
            {
              ...login,
              webAuthnResponse: JSON.stringify(
                verifyingWebauthnResponsePayload
              ),
            },
            pathName,
            recaptchaToken,
            turnstile
          )
        );
      } else {
        let credentialResponse, requestId;
        if (AssertionRequestWrapper) {
          /**
           * WebAuthn v1 with wrapper class
           */
          credentialResponse = await get({
            publicKey:
              AssertionRequestWrapper.publicKeyCredentialRequestOptions,
          });
          requestId = AssertionRequestWrapper.requestId;
        } else if (publicKeyCredentialRequestOptions) {
          /**
           * WebAuthn v1 without wrapper class
           */
          const credentialGetOptions = {
            publicKey: publicKeyCredentialRequestOptions,
          };
          credentialResponse = await get(credentialGetOptions);
          requestId = content.requestId;
        } else {
          dispatch(
            LoginAction.getTwoFaStatusFail({
              message: "Invalid WebAuthn challenge",
              type: "platform",
              status: "400",
              code: "local",
            })
          );
          return;
        }
        const verifyingWebauthnResponsePayload = {
          requestId,
          credential: credentialResponse,
        };
        dispatch(
          submitTwoFaLogin(
            {
              ...login,
              webAuthnResponse: JSON.stringify(
                verifyingWebauthnResponsePayload
              ),
            },
            pathName,
            recaptchaToken,
            turnstile
          )
        );
      }
    } catch (error) {
      if ((error as IErrorMessage)?.code === 0) {
        // web auth cancelled
        dispatch(LoginAction.webAuthCancelled());
        return;
      }
      if (error instanceof DOMException) {
        if (window.location.pathname === "/SSO/SAML2") {
          dispatch(LoginAction.clearAuthentication({ isReloadBrowser: true }));
        } else {
          dispatch(
            LoginAction.getTwoFaStatusFail({
              message: error.message,
              type: "platform",
              status: "400",
              code: "local",
            })
          );
        }
      } else {
        dispatch(
          LoginAction.getTwoFaStatusFail(
            (error as IErrorMessage)?.response?.data
          )
        );
        dispatch(LoginAction.clearAuthentication({ isReloadBrowser: false }));
      }
    }
  };

export const getWebAuthnChallenge =
  (username: string, samlSecret: string, failureMessage: string): AppThunk =>
  async (dispatch) => {
    try {
      const response = await axios.post("auth/challenge", {
        username,
        secret: samlSecret,
      });

      if (response.status === 204) {
        dispatch(
          LoginAction.getTwoFaStatusFail({
            code: "2fa:invalid_request",
            message: "invalid_request",
            type: "",
            status: "204",
          })
        );
      } else {
        dispatch(
          LoginAction.loginFail({
            err: {
              ...response.data,
              code: "1010",
              message: failureMessage,
            },
            pathname: "/",
          })
        );
      }
    } catch (error) {
      dispatch(
        LoginAction.getTwoFaStatusFail((error as IErrorMessage)?.response?.data)
      );
      dispatch(LoginAction.clearAuthentication({ isReloadBrowser: false }));
    }
  };

export const markAdvapacsViewerSeen = (): AppThunk => async (dispatch) => {
  dispatch(LoginAction.setShowIntroduceAdvaView(false));
  try {
    await axios.post(authenticatedUserApi.MARK);
  } catch (err) {
    // best-effort: modal already dismissed locally, safe to ignore
  }
};

export const getFeatures = (): AppThunk => async (dispatch) => {
  try {
    dispatch(LoginAction.getFeaturesStart());
    const data = await axiosInstanceRegion.get(`/features`);
    dispatch(LoginAction.getFeaturesSuccess(data.data.content as IFeature[]));
  } catch (err) {
    dispatch(
      LoginAction.getFeaturesFail((err as IErrorMessage)?.response?.data)
    );
  }
};

export const resolveBrandUrl = async (): Promise<IBrand> => {
  try {
    const pathParts = window.location.pathname.split("/").filter(Boolean);
    // we add a part brand before subSite, subSite should be indexed 1 now
    const isBrandRoute = pathParts[0] === "brand";
    const subSite = isBrandRoute ? pathParts[1] ?? "" : pathParts[0] ?? "";

    if (subSite !== "OAuthCallback")
      // @ts-ignore
      localStorage.setItem("brand_portal", undefined);
    const excludePaths = [
      "Signup",
      "OAuthCallback",
      "Login",
      "/",
      "Studies",
      "CriticalResults",
      "PACSDirectory",
      "Admin",
      "Users",
      "ResetPassword",
      "RegisterComplete",
    ];

    // excludes some sub site belong public client.
    if (!subSite || excludePaths.includes(subSite))
      // @ts-ignore
      return await Promise.resolve(null);

    // Create a single axios instance
    const apiClient = axios.create({
      timeout: 30000,
      baseURL: import.meta.env.VITE_APP_API_URL, // API base URL
      headers: {
        "X-TZ": Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    });

    // CALL GET REGION FROM SUB-SITE
    const response = await apiClient.get(`${brandApi.BRANDS}/${subSite}`);
    // @ts-ignore
    if (!response?.data?.region) return null;
    const isDev = APP_ENVIRONMENT === "development";
    // Fetch the brand data based on the region
    const regionUrl = `https://${response?.data?.region}${
      isDev ? "" : ".api"
    }.${API_BASE_URL}`;
    const brandResponse = await axios
      .create({ timeout: 60000, baseURL: regionUrl })
      .get(`${brandApi.BRANDS}/${subSite}`);
    // @ts-ignore
    if (!brandResponse?.data) return await Promise.resolve(null);
    const brandDataWithPath = {
      ...brandResponse.data,
      brandPath: subSite,
    };
    localStorage.setItem("brand_portal", JSON.stringify(brandDataWithPath));
    return await Promise.resolve({
      ...brandResponse.data,
      brandPath: subSite,
    });
  } catch (err) {
    // @ts-ignore
    return Promise.resolve(null);
  }
};
